import React, { useCallback, useEffect, useMemo, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";

import { Button, CredinTicket } from "@ui-kit/components/index.es";

import { resizableRoute } from "pages/_components/Resizable";
import ModifyPendingTransactionModal from "pages/transactions/_components/ModifyPendingTransactionModal";
import CredinTransactionData from "pages/transfer/_components/CredinTransactionData";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as formActions } from "reducers/form";
import { actions as layoutActions } from "reducers/layout";
import { actions as debinActions, selectors as debinSelectors } from "reducers/debin";
import {
    actions as frequentDestinationActions,
    selectors as frequentDestinationSelectors,
} from "reducers/frequentDestination";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { actions as transactionsActions, selectors as transactionsSelectors } from "reducers/transactions";

import * as config from "util/config";
import * as i18n from "util/i18n";
import {
    canUserSignPendingFromPotentialSchemes,
    exportGenericTicketPdfWithSuccessMessage,
    isFinalState,
    isMvp3PendingTransactionsEnabled,
    isTransactionPendingProcessing,
} from "util/transaction";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";
import {
    useEmbeddedV3ShellLayoutSync,
    isCredinV3Enabled,
    isUxDashboardImprovementsEnabled,
    resolveEmbeddedV3GoBackPath,
} from "util/v3ShellLayoutSync";
import {
    getCredinTicketPath,
    getGenerateCredinPath,
    getPostLoginHomePath,
    getRequestCredinPath,
} from "util/v3UxConfig";

import {
    buildCredinTicketPdfItems,
    formatCredinTicketSignatureDate,
    getCredinTicketLabel,
    getCredinTicketTitle,
    getCredinTicketTitleKey,
    mapCredinCancelledBy,
    mapCredinSummaryRecipients,
    mapCredinSummaryToTicketData,
    resolveCredinDebitAccountLabel,
} from "v3/pages/transfers/generateCredinTicket";

const CREDIN_TICKET_SCOPES = ["forms", "credin", "debin"];

/**
 * V3 Credin ticket screen. Pending footer mirrors legacy `TransactionTicket.renderButtonArray`
 * + `checkForActionButtons`: pending list, delete, modify, sign or go home.
 */
function CredinTicketV3Page(props) {
    const {
        dispatch,
        isDesktop,
        summary,
        accounts,
        loggedUser,
        embeddedInV3Shell,
        potentialSignaturesData,
        administrationScheme,
        lastHrefPathname,
        transactionRoute,
        activeEnvironment,
        frequentDestination,
        idEnvironment,
    } = props;

    const [isModalDisplayed, setIsModalDisplayed] = useState(false);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);

    const isPending = summary?.idTransactionStatus === "PENDING";
    const isFinished = summary?.idTransactionStatus === "FINISHED";
    const isPendingProcessing = useMemo(() => (summary ? isTransactionPendingProcessing(summary) : false), [summary]);

    useEffect(() => {
        if (!summary?.idTransactionStatus && localStorage.getItem("flow")) {
            dispatch(push(`${JSON.parse(localStorage.getItem("flow"))}`));
            return;
        }
        if (loggedUser?.userId) {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
    }, [dispatch, loggedUser?.userId, summary?.idTransactionStatus]);

    useEffect(() => {
        if (summary?.idTransaction) {
            dispatch(transactionsActions.getPotentialSignaturesData(summary.idTransaction));
        }
    }, [dispatch, summary?.idTransaction]);

    /**
     * The ticket is reachable from the CREDIN creation form, the transactions history and the pending
     * authorizations list; back returns to the actual origin instead of always to the creation form.
     * After cancel, lastHref may be confirmation/form — fall back to the list saved in transactionRoute.
     */
    const goBackPath = useMemo(() => {
        const fromEmbedded = resolveEmbeddedV3GoBackPath(getRequestCredinPath(), lastHrefPathname);
        const lastHrefIsCredinFlow =
            lastHrefPathname === getRequestCredinPath() ||
            lastHrefPathname === getGenerateCredinPath() ||
            lastHrefPathname === getCredinTicketPath();
        if (summary?.idTransactionStatus === "CANCELLED" && lastHrefIsCredinFlow) {
            return resolveDraftTransactionListGoBack(lastHrefPathname, transactionRoute);
        }
        return fromEmbedded;
    }, [lastHrefPathname, summary?.idTransactionStatus, transactionRoute]);

    const ticketTitleKey = getCredinTicketTitleKey(summary);
    const ticketTitle = useMemo(() => getCredinTicketTitle(summary), [summary]);

    /** Mobile signature control banner, aligned with the transfer ticket. */
    const titleHintText = useMemo(() => {
        if (!isPending || !summary?.control) {
            return undefined;
        }
        const text = config.get("frontend.mobileSignatureControl.text", "");
        return text || undefined;
    }, [isPending, summary?.control]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: isDesktop ? "" : ticketTitleKey,
        goBackPath: isDesktop ? null : goBackPath,
        scopes: CREDIN_TICKET_SCOPES,
    });

    const getLabels = useCallback((id) => i18n.get(id), []);

    const debitAccountLabel = useMemo(() => resolveCredinDebitAccountLabel(summary, accounts), [accounts, summary]);

    const recipientInputs = useMemo(() => mapCredinSummaryRecipients(summary?.recipients), [summary?.recipients]);

    const credinData = useMemo(
        () => ({
            ...mapCredinSummaryToTicketData(summary, accounts),
            debitAccountLabel,
        }),
        [accounts, debitAccountLabel, summary],
    );

    const primaryRecipient = recipientInputs[0] ?? {};
    const additionalRecipients = recipientInputs.slice(1);
    const rawPrimaryRecipient = Array.isArray(summary?.recipients) ? summary.recipients[0] : summary?.recipients;
    const beneficiaryCbu = primaryRecipient.cbu ?? rawPrimaryRecipient?.cbu;
    const beneficiaryAlias = rawPrimaryRecipient?.alias;

    useEffect(() => {
        const hasPermission = activeEnvironment?.permissions?.frequentDestination;
        if (beneficiaryCbu && hasPermission && idEnvironment) {
            dispatch(frequentDestinationActions.getFrequentDestinationRequest(idEnvironment, beneficiaryCbu));
        }
    }, [activeEnvironment, beneficiaryCbu, dispatch, idEnvironment]);

    const handleCreateFrequentDestination = useCallback(() => {
        let cbuAlias = "";
        if (beneficiaryCbu) {
            cbuAlias = beneficiaryCbu;
        }
        if (beneficiaryAlias) {
            cbuAlias = beneficiaryAlias;
        }
        dispatch(frequentDestinationActions.setFrequentDestination({ cbu: cbuAlias }));
        dispatch(push("/v2/agenda/create"));
    }, [beneficiaryAlias, beneficiaryCbu, dispatch]);

    const showFrequentDestinationButton = useMemo(() => {
        if (additionalRecipients.length > 0) {
            return false;
        }
        const hasPermission = activeEnvironment?.permissions?.frequentDestination;
        const isAlreadySaved = Boolean(frequentDestination?.cbu && frequentDestination.cbu === beneficiaryCbu);
        return Boolean(hasPermission && !isAlreadySaved && (beneficiaryCbu || beneficiaryAlias));
    }, [activeEnvironment, additionalRecipients.length, beneficiaryAlias, beneficiaryCbu, frequentDestination]);

    const transactionMoment = useMemo(() => {
        if (!summary) {
            return undefined;
        }
        return summary.creationDate ?? summary.creationDateTime;
    }, [summary]);

    const preparedBy = useMemo(
        () => ({
            userFullName: summary?.creatorFullName || loggedUser?.userFullName || "",
            creationDateTime: transactionMoment,
        }),
        [loggedUser?.userFullName, summary?.creatorFullName, transactionMoment],
    );

    const signedBy = useMemo(() => {
        if (!summary?.signatures?.length) {
            return [];
        }
        return summary.signatures.map((s) => ({
            userFirstName: s.userFirstName,
            userLastName: s.userLastName,
            userFullName: s.userFullName,
            creationDateTime: s.creationDateTime ?? s.modificationDateTime ?? s.submitDateTime ?? s.creationDate,
        }));
    }, [summary?.signatures]);

    const cancelledBy = useMemo(() => mapCredinCancelledBy(summary), [summary]);

    const credinTicketLegacyContent = useMemo(() => {
        if (!summary || !debitAccountLabel || debitAccountLabel === "-") {
            return null;
        }

        if (Array.isArray(summary.recipients)) {
            return summary.recipients.map((recipient, index) => (
                <CredinTransactionData
                    key={recipient.id || recipient.cbu || String(index)}
                    summary={summary}
                    isDesktop={isDesktop}
                    recipient={recipient}
                    accountName={debitAccountLabel}
                />
            ));
        }

        return (
            <CredinTransactionData
                summary={summary}
                isDesktop={isDesktop}
                recipient={summary.recipients}
                accountName={debitAccountLabel}
            />
        );
    }, [debitAccountLabel, isDesktop, summary]);

    const showSignButton = useMemo(() => {
        if (!summary || !isPending || isPendingProcessing || summary.control) {
            return false;
        }
        return canUserSignPendingFromPotentialSchemes(summary, loggedUser, potentialSignaturesData);
    }, [summary, isPending, isPendingProcessing, loggedUser, potentialSignaturesData]);

    const exportPdf = useCallback(async () => {
        if (!summary || !debitAccountLabel || debitAccountLabel === "-") {
            return;
        }

        setExportPdfLoading(true);
        try {
            const rawRecipients = Array.isArray(summary.recipients) ? summary.recipients : [summary.recipients];
            const ticketBody = rawRecipients
                .map((recipient) => buildCredinTicketPdfItems(summary, recipient, debitAccountLabel))
                .reduce((accumulator, element) => accumulator.concat(element), []);

            await exportGenericTicketPdfWithSuccessMessage(
                {
                    ticketName: ticketTitle,
                    ticketBody,
                },
                dispatch,
                i18n.get("global.download.success"),
            );
        } finally {
            setExportPdfLoading(false);
        }
    }, [debitAccountLabel, dispatch, summary, ticketTitle]);

    const rightContentMobile = useMemo(() => {
        if (!isFinished) {
            return null;
        }
        return (
            <Button
                aria-label={i18n.get("global.download.ticket")}
                variant="text"
                color="white"
                icon="SendDown"
                iconVariant="outline"
                onClick={exportPdf}
                loading={exportPdfLoading}
            />
        );
    }, [exportPdf, exportPdfLoading, isFinished]);

    useEffect(() => {
        if (!embeddedInV3Shell || !isCredinV3Enabled() || isDesktop) {
            return undefined;
        }
        dispatch(layoutActions.setRightContentMobile(rightContentMobile));
        return () => {
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, rightContentMobile]);

    const handleGoBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const goToRequestCredin = useCallback(() => {
        dispatch(push(getRequestCredinPath()));
    }, [dispatch]);

    const goToHistoricTransactions = useCallback(() => {
        dispatch(transactionsActions.setFilters(null));
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const goToPendingAuthorizations = useCallback(() => {
        dispatch(push("/pendingTransaction/list"));
        if (!isMvp3PendingTransactionsEnabled() && summary?.idActivity) {
            dispatch(transactionsActions.initPendings());
            dispatch(
                transactionsActions.loadListRequest(
                    {
                        pageNumber: 1,
                        status: "PENDING",
                        filterPendingDispatch: false,
                        functionRequest: "pendingTransactions",
                        idActivity: summary.idActivity,
                    },
                    true,
                    false,
                ),
            );
        }
    }, [dispatch, summary?.idActivity]);

    const goToHome = useCallback(() => {
        dispatch(push(getPostLoginHomePath(isUxDashboardImprovementsEnabled())));
    }, [dispatch]);

    const cancelPendingTransaction = useCallback(() => {
        if (!summary) {
            return;
        }
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: summary.idTransaction,
                content: credinTicketLegacyContent,
                title: ticketTitleKey,
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity: summary.idActivity, idForm: "" }));
        dispatch(debinActions.loadConfirmCredin({ ...summary, action: "cancel" }));
        dispatch(
            push({
                pathname: getGenerateCredinPath(),
                state: { goBackState: getCredinTicketPath() },
            }),
        );
    }, [credinTicketLegacyContent, dispatch, summary, ticketTitleKey]);

    const signTransaction = useCallback(() => {
        if (!summary) {
            return;
        }
        dispatch(
            push({
                pathname: getGenerateCredinPath(),
                state: { goBackState: getCredinTicketPath() },
            }),
        );
        dispatch(
            formActions.signTransactionData({
                idTransaction: summary.idTransaction,
                idActivity: summary.idActivity,
                idForm: "",
                content: credinTicketLegacyContent,
                title: ticketTitleKey,
                pendingDispatch: summary.pendingDispatch,
                useTransactionData: true,
            }),
        );
        dispatch(
            formActions.signTransactionPreview({
                idActivity: summary.idActivity,
                idTransaction: summary.idTransaction,
                idForm: "",
                ticketData: summary,
            }),
        );
        dispatch(debinActions.loadConfirmCredin({ ...summary, action: "sign" }));
    }, [credinTicketLegacyContent, dispatch, summary, ticketTitleKey]);

    const navigationButtons = useMemo(() => {
        if (!isFinished || !summary || !debitAccountLabel || debitAccountLabel === "-") {
            return [];
        }

        return [
            {
                onClick: exportPdf,
                icon: "SendDown",
                label: getLabels("global.download"),
                loading: exportPdfLoading,
                css: { paddingLeft: 40, paddingRight: 40 },
            },
        ];
    }, [debitAccountLabel, exportPdf, exportPdfLoading, getLabels, isFinished, summary]);

    const footerNavigationButtons = useMemo(() => {
        if (!summary) {
            return [];
        }

        if (isPending) {
            const buttons = [
                {
                    onClick: goToPendingAuthorizations,
                    icon: "WritingPending",
                    label: getLabels("goto.pendings.transaction.autorizaciones"),
                    variant: "outline",
                },
            ];

            if (!isPendingProcessing && !summary.control) {
                buttons.push({
                    onClick: cancelPendingTransaction,
                    icon: "Trash",
                    label: getLabels("transaction.delete"),
                    variant: "outline",
                });
            }

            if (!isPendingProcessing) {
                buttons.push({
                    onClick: () => setIsModalDisplayed(true),
                    icon: "Pencil",
                    label: getLabels("global.modify"),
                    variant: "outline",
                });
            }

            if (!isPendingProcessing) {
                if (showSignButton) {
                    buttons.push({
                        onClick: signTransaction,
                        icon: "AddServices",
                        label: getLabels("forms.signTransaction.link"),
                        variant: "solid",
                    });
                } else {
                    buttons.push({
                        onClick: goToHome,
                        icon: "Home",
                        label: getLabels("global.goToHome"),
                        variant: "solid",
                    });
                }
            }

            return buttons;
        }

        if (isFinalState(summary.idTransactionStatus)) {
            return [
                {
                    onClick: goToHistoricTransactions,
                    icon: "Refund",
                    label: getLabels("goto.historic.transaction"),
                    variant: "outline",
                },
                {
                    onClick: goToRequestCredin,
                    icon: "Home",
                    label: getCredinTicketLabel("goToRequestCredin"),
                    variant: "solid",
                },
            ];
        }

        return [];
    }, [
        cancelPendingTransaction,
        getLabels,
        goToHistoricTransactions,
        goToHome,
        goToPendingAuthorizations,
        goToRequestCredin,
        isPending,
        isPendingProcessing,
        showSignButton,
        signTransaction,
        summary,
    ]);

    if (!summary?.idTransactionStatus) {
        return null;
    }

    return (
        <>
            <CredinTicket
                goBack={handleGoBack}
                getLabels={getLabels}
                credinData={credinData}
                primaryRecipient={primaryRecipient}
                additionalRecipients={additionalRecipients}
                formatOperationDate={formatLegacyWebDateTimeString}
                transactionTicketProps={{
                    titleTransaction: ticketTitle,
                    formatSignatureDate: formatCredinTicketSignatureDate,
                }}
                titleHintText={titleHintText}
                preparedBy={preparedBy}
                signedBy={signedBy}
                cancelledBy={cancelledBy}
                navigationButtons={navigationButtons}
                footerNavigationButtons={footerNavigationButtons}
                potentialSignaturesData={potentialSignaturesData}
                signatureSchemeDisplayMode={administrationScheme === "medium" ? "medium" : "advanced"}
                onCreateFrequentDestination={
                    showFrequentDestinationButton ? handleCreateFrequentDestination : undefined
                }
            />
            <ModifyPendingTransactionModal
                isDesktop={isDesktop}
                isDisplayed={isModalDisplayed}
                handleDismiss={() => setIsModalDisplayed(false)}
                dispatch={dispatch}
                idTransaction={summary?.idTransaction}
            />
        </>
    );
}

CredinTicketV3Page.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    summary: shape({}),
    accounts: shape({}).isRequired,
    loggedUser: shape({
        userId: string,
        userFullName: string,
    }).isRequired,
    embeddedInV3Shell: bool,
    potentialSignaturesData: shape({}),
    administrationScheme: string,
    lastHrefPathname: string,
    transactionRoute: string,
    activeEnvironment: shape({}),
    frequentDestination: shape({}),
    idEnvironment: string,
};

CredinTicketV3Page.defaultProps = {
    summary: null,
    embeddedInV3Shell: false,
    potentialSignaturesData: null,
    administrationScheme: "advanced",
    lastHrefPathname: undefined,
    transactionRoute: "",
    activeEnvironment: null,
    frequentDestination: null,
    idEnvironment: "",
};

const mapStateToProps = (state) => ({
    summary: debinSelectors.getSummary(state),
    accounts: accountsSelectors.getAccounts(state),
    loggedUser: sessionSelectors.getUser(state),
    potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
    administrationScheme: sessionSelectors.getAdministrationScheme(state),
    lastHrefPathname: statusSelectors.getPathname(state),
    transactionRoute: transactionsSelectors.getTransactionRoute(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    frequentDestination: frequentDestinationSelectors.getFrequentDestination(state),
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(CredinTicketV3Page);
